import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { HelmCompass } from "@/components/helm-logo";
import { TosClickwrap } from "@/components/tos/tos-clickwrap";
import { apiService } from "@/lib/api-service";
import { authService } from "@/lib/auth-service";
import { trackTosAccepted, trackTosCancelled } from "@/lib/analytics-events";

export const Route = createFileRoute("/_app/_/onboarding/accept-tos")({
	component: AcceptTosPage,
	loader: async () => {
		const status = await apiService.getTosStatus();
		if (!status.needs_acceptance) {
			throw redirect({ to: "/onboarding/get-started" });
		}
		return { tosStatus: status };
	},
});

function AcceptTosPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { tosStatus } = Route.useLoaderData();
	const [error, setError] = useState<string | null>(null);

	const acceptMutation = useMutation({
		mutationFn: (args: { tosVersion: string; privacyVersion: string }) =>
			apiService.acceptTos({
				tos_version: args.tosVersion,
				privacy_policy_version: args.privacyVersion,
				checkbox_confirmed: true,
			}),
		onSuccess: (_data, variables) => {
			trackTosAccepted(variables.tosVersion);
			queryClient.invalidateQueries({ queryKey: ["session"] });
			navigate({ to: "/onboarding/get-started" });
		},
		onError: () => {
			setError("Something went wrong. Please try again.");
		},
	});

	const handleCancel = () => {
		trackTosCancelled();
		authService.clearTokens();
		navigate({ to: "/login" });
	};

	return (
		<div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 bg-[var(--strategy-surface)]">
			<div className="max-w-xl w-full space-y-10">
				{/* Logo */}
				<div className="flex flex-col items-center">
					<div className="flex items-center gap-6">
						<HelmCompass size={56} strokeWidth={1} />
						<span
							className="font-display text-4xl"
							style={{ letterSpacing: "0.25em" }}
						>
							H E L M
						</span>
					</div>
				</div>

				{/* Divider */}
				<div className="border-t border-border mx-auto w-48" />

				{/* Heading */}
				<div className="text-center space-y-2">
					<p className="text-xl md:text-2xl text-muted-foreground">
						Before we begin
					</p>
					<p className="text-lg text-muted-foreground/80 leading-relaxed">
						Please review and accept our Terms of Service and Privacy
						Policy to continue.
					</p>
				</div>

				{/* Error */}
				{error && (
					<div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
						{error}
					</div>
				)}

				{/* Clickwrap */}
				<TosClickwrap
					tosVersion={tosStatus.tos_version}
					privacyVersion={tosStatus.privacy_version}
					onAccept={(tosVersion, privacyVersion) => {
						setError(null);
						acceptMutation.mutate({ tosVersion, privacyVersion });
					}}
					onCancel={handleCancel}
					isPending={acceptMutation.isPending}
				/>
			</div>
		</div>
	);
}
